import React, { Suspense } from 'react';
import App from './app/App';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { authProvider } from './authProvider';
import { MsalProvider } from '@azure/msal-react';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import LoadingIndicator from './app/shared/LoadingIndicator';
import { SnackbarProvider } from 'material-ui-snackbar-provider';
import CustomSnackBar from './app/shared/SnackBar/CustomSnackBar';
import { createRoot } from 'react-dom/client';
import { Helmet, HelmetProvider } from 'react-helmet-async';
import { getConfiguration, isDevEnvironment } from './app/shared/utils/configuration';
import { isClientError } from './app/shared/utils/apiErrors';
import appConfig from '../app-config-prod.json';
import './firstOrDefault';
import './app/theme/fonts.css';
import './index.css';
import './i18n/config';

export type Config = typeof appConfig;

const DEFAULT_QUERY_RETRIES = 3;

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 1000 * 60 * 60,
      refetchOnWindowFocus: false,
      // Everything keeps react-query's usual three attempts except a 4xx: the server understood and
      // said no, and it will say the same thing however many times it is asked. Retrying only holds
      // the user on a spinner before showing a message that was already available. 5xx and dropped
      // connections are the ones a retry can fix, and they still get the full count.
      retry: (failureCount, error) => !isClientError(error) && failureCount < DEFAULT_QUERY_RETRIES,
    },
  },
});

const initApp = (Component: React.FC): void => {
  fetch('/app-config.json')
    .then((response) => response.json())
    .then((config) => {
      renderApp(Component, config);
    });
};

const renderApp = (Component: React.FC, config: Config): void => {
  window.APP_CONFIG = config;

  const msalInstance = authProvider.getMsalAuthProvider();

  const favicon = getConfiguration<string>('head.favicon', '/favicon.ico');
  const environment = getConfiguration<string>('environment', '');
  const isDev = isDevEnvironment();

  const container = document.getElementById('root');
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const root = createRoot(container!);
  root.render(
    <Suspense fallback={<LoadingIndicator />}>
      <React.StrictMode>
        <SnackbarProvider
          SnackbarComponent={CustomSnackBar}
          SnackbarProps={{
            anchorOrigin: { vertical: 'top', horizontal: 'center' },
            autoHideDuration: 3000,
          }}
        >
          <MsalProvider instance={msalInstance}>
            <QueryClientProvider client={queryClient}>
              <HelmetProvider>
                <Helmet>
                  {/* eslint-disable-next-line i18next/no-literal-string */}
                  <title>Kotipizza Juuri{isDev ? ` - ${environment}` : ''}</title>
                  <link rel="icon" href={favicon} />
                </Helmet>
                <Component />
                <ReactQueryDevtools initialIsOpen={false} />
              </HelmetProvider>
            </QueryClientProvider>
          </MsalProvider>
        </SnackbarProvider>
      </React.StrictMode>
    </Suspense>
  );
};

initApp(App);
